// Hub ▸ Marketing ▸ Channels & Campaigns (slice S2, CEO 2026-10-21):
//   1. dates at the top, then performance by channel → campaign: spend,
//      leads, orders, gross profit, POAS, cost to win a client (clicks, CPC,
//      cost per lead and revenue behind "More columns"), sortable, crediting
//      the last or first ad clicked, CSV export; campaigns as cards on phones;
//   2. a compact connection strip — it only opens up for a problem; setting
//      names are shown to admins only;
//   3. spend upload (LinkedIn, Meta, ChatGPT, sample packs) behind a button —
//      admin + marketing (finance reads only);
//   4. (S5) a "Website" view beside it: ad impressions → clicks → visits →
//      quote requests → paid orders (WebsiteFunnel.tsx); the connection strip
//      shows the nightly Klaviyo / Google Analytics imports, and admins can
//      run one now.
// Spend is the analytics layer's (the same spend the Overview adds up).
// Marketing-only viewers (CEO 2026-10-22): revenue and ROAS in place of gross
// profit and POAS — the API sends them no profit field.
import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Upload, X } from "lucide-react";
import { useAuth } from "@/auth/AuthProvider";
import { api, apiErrorMessage } from "@/lib/api";
import { canEditMarketing } from "@/lib/access";
import { cn } from "@/lib/utils";
import { usePeriodParam } from "./OverviewTab";
import {
  EmptyState, ErrorLine, InfoTip, Num, Panel, PeriodPicker, Pill, Segmented, Skeleton, SortTable, count, downloadText, money, periodQuery, shortDay, toCsv, useApi,
  type Column, type Tone,
} from "./kit";
import { RATIO_BARS, WORDS, ratioOf, valueOf } from "./measure";
import type { Connection, Measure, PerfRow, PerformanceReady, PerformanceResponse } from "./types";
import { WebsiteFunnel } from "./WebsiteFunnel";

const STATE: Record<Connection["state"], { tone: Tone; label: string }> = {
  connected: { tone: "ok", label: "Connected" }, not_connected: { tone: "neutral", label: "Not connected" },
  paused: { tone: "risk", label: "Paused" }, off: { tone: "warn", label: "Switched off" }, waiting: { tone: "warn", label: "Waiting" },
  failed: { tone: "risk", label: "Last import failed" },
};
/** The nightly imports an admin can run now (S5). */
const SYNCABLE = new Set(["klaviyo", "ga4"]);
function SyncNow({ id, onDone }: { id: string; onDone: () => void }) {
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true); setMsg(null);
    try { const r = await api<{ detail?: string }>(`/api/marketing/feeds/${id}/sync`, { method: "POST" }); setMsg(r.detail ?? "Started — this takes a minute or two. Refresh to see the result."); setTimeout(onDone, 90_000); }
    catch (e) { setMsg(apiErrorMessage(e, "Couldn't start the import.")); }
    finally { setBusy(false); }
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button type="button" disabled={busy} onClick={go} className="h-9 rounded-md border border-rule px-3 text-label hover:bg-paper disabled:opacity-40">Import now</button>
      {msg && <span role="status" className="text-meta text-ink-muted">{msg}</span>}
    </div>
  );
}

/** One line of status dots; a platform with a problem opens its own card. */
function ConnectionStrip({ isAdmin }: { isAdmin: boolean }) {
  const { data, error, loading, reload } = useApi<{ connections: Connection[] }>("/api/marketing/connections");
  const [openAll, setOpenAll] = useState(false);
  if (error) return <ErrorLine onRetry={reload}>{error}</ErrorLine>;
  if (loading && !data) return <Skeleton rows={0} label="Loading connections" />;
  const list = data?.connections ?? [];
  // Google Analytics not connected always opens its card (the Website view sends people here to connect it).
  const problems = list.filter((c) => c.state === "paused" || c.state === "off" || c.state === "failed" || (c.state === "not_connected" && c.how === "api"));
  const shown = openAll ? list : problems;
  return (
    <div className="space-y-3">
      <ul className="flex flex-wrap items-center gap-x-4 gap-y-2" aria-label="Connections">
        {list.map((c) => (
          <li key={c.id} className="flex items-center gap-1.5 text-label" title={c.lastDataAt ? `Last import ${new Date(c.lastDataAt).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}` : c.summary}>
            {/* Connected platforms show their name; anything else says its state in words too ("nothing uploaded yet" for an upload platform with no file). */}
            {c.how === "csv" && c.state === "waiting" && /nothing uploaded yet/.test(c.summary) ? <Pill tone="neutral">{c.name}<span className="font-normal"> · nothing uploaded yet</span></Pill> :
            <Pill tone={STATE[c.state].tone}>{c.name}{c.state !== "connected" && <span className="font-normal"> · {STATE[c.state].label.toLowerCase()}</span>}</Pill>}
            {c.state === "connected" && <span className="sr-only">connected</span>}
          </li>
        ))}
        <li><button type="button" onClick={() => setOpenAll((o) => !o)} className="text-meta font-semibold underline underline-offset-4">{openAll ? "Hide details" : "Details"}</button></li>
      </ul>
      {shown.length > 0 && (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2 2xl:grid-cols-4">
          {shown.map((c) => (
            <li key={c.id} className="flex flex-col gap-1.5 rounded-lg border border-rule bg-white p-4">
              <div className="flex items-start justify-between gap-2">
                <p className="text-label font-semibold text-ink">{c.name}</p>
                <Pill tone={STATE[c.state].tone}>{STATE[c.state].label}</Pill>
              </div>
              <p className="text-label text-ink">{c.summary}</p>
              {isAdmin && c.adminNote && <p className="text-meta text-ink-muted">{c.adminNote}</p>}
              {isAdmin && c.needs.length > 0 && (
                <ul className="flex flex-wrap gap-1.5">{c.needs.map((n) => <li key={n}><code className="rounded bg-paper px-1.5 py-0.5 text-meta text-ink">{n}</code></li>)}</ul>
              )}
              {isAdmin && SYNCABLE.has(c.id) && (c.state === "connected" || c.state === "failed" || (c.state === "waiting" && !c.adminNote)) && <SyncNow id={c.id} onDone={reload} />}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const CSV_PLATFORMS = [{ value: "linkedin", label: "LinkedIn" }, { value: "meta", label: "Meta" }, { value: "chatgpt", label: "ChatGPT" }, { value: "other", label: "Sample packs" }];

function SpendUpload({ onClose }: { onClose: () => void }) {
  const [platform, setPlatform] = useState("linkedin");
  const [csv, setCsv] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  async function upload() {
    setBusy(true); setMsg(null);
    try {
      const r = await api<{ saved: number }>("/reports/advertising/spend-csv", { method: "POST", body: JSON.stringify({ csv, platform }) });
      setMsg({ ok: true, text: `Saved ${r.saved} day${r.saved === 1 ? "" : "s"} of spend. It shows in these numbers after tonight's rebuild.` });
      setCsv("");
    } catch (e) { setMsg({ ok: false, text: apiErrorMessage(e, "Couldn't save the spend. Nothing was changed.") }); }
    finally { setBusy(false); }
  }
  return (
    <Panel title="Upload spend" actions={<button type="button" onClick={onClose} aria-label="Close upload" className="rounded-md p-2 text-ink-muted hover:bg-paper"><X className="h-5 w-5" /></button>}>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[1fr_1.2fr]">
        <div className="space-y-3 text-label text-ink">
          <p>For LinkedIn, Meta, ChatGPT and sample packs, export the daily campaign report in US dollars and upload it here.</p>
          <p className="text-ink-muted">The first row names the columns: <code className="rounded bg-paper px-1">date</code>, <code className="rounded bg-paper px-1">campaign</code>, <code className="rounded bg-paper px-1">cost</code> — and <code className="rounded bg-paper px-1">clicks</code>, <code className="rounded bg-paper px-1">impressions</code> if you have them. Uploading the same day and campaign again replaces it, so re-uploading a month is safe.</p>
        </div>
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <label className="text-label font-semibold" htmlFor="csv-platform">Platform</label>
            <select id="csv-platform" value={platform} onChange={(e) => setPlatform(e.target.value)} className="h-10 rounded-md border border-rule bg-white px-3 text-label">
              {CSV_PLATFORMS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <label className="inline-flex h-10 cursor-pointer items-center gap-1.5 rounded-md border border-rule px-3 text-label hover:bg-paper focus-within:outline focus-within:outline-2 focus-within:outline-focus">
              <Upload className="h-4 w-4" aria-hidden />Choose file
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="sr-only" onChange={async (e) => { const f = e.target.files?.[0]; if (f) setCsv(await f.text()); if (fileRef.current) fileRef.current.value = ""; }} />
            </label>
          </div>
          <label className="sr-only" htmlFor="csv-text">CSV</label>
          <textarea id="csv-text" value={csv} onChange={(e) => setCsv(e.target.value)} rows={5} placeholder={"date,campaign,cost,clicks,impressions\n2026-10-01,Law firms — Q4,84.20,31,4120"}
            className="w-full rounded-md border border-rule p-3 font-mono text-meta" />
          <div className="flex flex-wrap items-center gap-3">
            <button type="button" disabled={!csv.trim() || busy} onClick={upload} className="h-11 rounded-md bg-ink px-5 text-label font-semibold text-white disabled:opacity-40">Upload spend</button>
            {csv && <span className="text-meta text-ink-muted">{csv.trim().split(/\r?\n/).length - 1} row(s) ready</span>}
          </div>
          {msg && <p role="status" className={cn("rounded-md px-4 py-3 text-label", msg.ok ? "bg-ok-bg text-ok" : "bg-risk-bg text-risk")}>{msg.text}</p>}
        </div>
      </div>
    </Panel>
  );
}

// ─── performance ────────────────────────────────────────────────────────────

/** POAS (admin + finance) or ROAS (marketing-only), coloured by that measure's bars. */
function RatioCell({ r, m }: { r: PerfRow; m: Measure }) {
  if (valueOf(r, m) === null) return <span className="text-ink-muted">—</span>;
  if (r.zeroBack) return <span className="font-semibold text-risk">$0 back</span>;
  const v = ratioOf(r, m);
  if (v === null) return <span className="text-ink-muted">—</span>;
  const b = RATIO_BARS[m];
  return <span className={cn(v < b.low ? "font-semibold text-risk" : v >= b.high ? "font-semibold text-ok" : "")}>{v.toFixed(2)}×</span>;
}

function perfColumns(f: Record<string, string>, more: boolean, m: Measure): Column<PerfRow>[] {
  const name: Column<PerfRow> = { key: "name", label: "Channel / campaign", value: (r) => r.campaign ?? r.channelLabel, render: (r) => r.campaign === null
    ? <span><span className="font-semibold">{r.channelLabel}</span>{r.spendSource && <span className="block text-meta font-normal text-ink-muted">spend by {r.spendSource === "api" ? "API" : "upload"}</span>}</span>
    : <span className="text-ink">{r.campaign}</span> };
  const c = (key: string, label: string, fk: string, get: (r: PerfRow) => number | null, show: (r: PerfRow) => React.ReactNode): Column<PerfRow> => ({ key, label, numeric: true, info: f[fk], value: get, render: (r) => <Num v={get(r)}>{show(r)}</Num> });
  return [
    name,
    c("spend", "Spend", "spend", (r) => r.spendCents, (r) => money(r.spendCents)),
    c("leads", "Leads", "leads", (r) => r.leads, (r) => count(r.leads)),
    c("orders", "Orders", "orders", (r) => r.orders, (r) => count(r.orders)),
    m === "revenue"
      ? c("gp", "Revenue (credited ad)", "revenue", (r) => r.revenueCents, (r) => money(r.revenueCents))
      : c("gp", "Gross profit (credited ad)", "gp", (r) => r.gpCents ?? null, (r) => `${money(r.gpCents ?? null)}${r.gpPendingOrders && r.gpCents != null ? "*" : ""}`),
    { key: "poas", label: `${WORDS[m].ratio} (credited ad)`, numeric: true, info: m === "revenue" ? f.roas : f.poas, value: (r) => ratioOf(r, m), render: (r) => <RatioCell r={r} m={m} /> },
    c("cac", "Cost to win a client", "cac", (r) => r.cacCents, (r) => money(r.cacCents)),
    ...(more ? [
      c("clicks", "Clicks", "clicks", (r) => r.clicks, (r) => count(r.clicks)),
      c("cpc", "CPC", "cpc", (r) => r.cpcCents, (r) => money(r.cpcCents, { cents: true })),
      c("cpl", "Cost / lead", "cost_per_lead", (r) => r.costPerLeadCents, (r) => money(r.costPerLeadCents)),
      ...(m === "revenue" ? [] : [c("revenue", "Revenue", "revenue", (r) => r.revenueCents, (r) => money(r.revenueCents))]),
    ] : []),
  ];
}

/** Phones: each campaign as a card, grouped under its channel. */
function PerfCards({ d }: { d: PerformanceReady }) {
  const m = d.measure;
  const chans = [...d.channels].sort((a, b) => (valueOf(b, m) ?? 0) - (valueOf(a, m) ?? 0) || b.spendCents - a.spendCents);
  return (
    <div className="space-y-5 sm:hidden">
      {chans.map((ch) => (
        <section key={ch.channel}>
          <h3 className="flex items-baseline justify-between text-label font-semibold"><span>{ch.channelLabel}</span><span className="tabular-nums">{money(ch.spendCents)} · <RatioCell r={ch} m={m} /></span></h3>
          <ul className="mt-2 space-y-2">
            {d.campaigns.filter((c) => c.channel === ch.channel).sort((a, b) => (valueOf(b, m) ?? 0) - (valueOf(a, m) ?? 0)).map((c) => (
              <li key={`${c.channel}|${c.campaign}`} className="rounded-md border border-rule p-3">
                <p className="font-semibold text-ink">{c.campaign ?? "(no campaign)"}</p>
                <dl className="mt-2 grid grid-cols-3 gap-2 text-label">
                  {[["Spend", money(c.spendCents)], ["Leads", count(c.leads)], ["Orders", count(c.orders)], [WORDS[m].value, money(valueOf(c, m))], ["Cost to win", money(c.cacCents)]].map(([k, v]) => (
                    <div key={k}><dt className="text-meta text-ink-muted">{k}</dt><dd className="tabular-nums">{v}</dd></div>
                  ))}
                  <div><dt className="text-meta text-ink-muted">{WORDS[m].ratio}</dt><dd><RatioCell r={c} m={m} /></dd></div>
                </dl>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function PerfTable({ d }: { d: PerformanceReady }) {
  const [more, setMore] = useState(false);
  if (!d.channels.length) return <EmptyState title="No spend, leads or orders in these dates">Rows appear once ad spend arrives (Google and Microsoft automatically, the others by upload) or a tagged website visit becomes a lead or an order.</EmptyState>;
  const cols = perfColumns(d.formulas, more, d.measure);
  const totals = d.totals;
  return (
    <div className="space-y-3">
      <div className="hidden justify-end sm:flex">
        <button type="button" aria-pressed={more} onClick={() => setMore((m) => !m)} className="h-9 rounded-md border border-rule px-3 text-label hover:bg-paper">{more ? "Fewer columns" : d.measure === "revenue" ? "More columns (clicks, CPC, cost per lead)" : "More columns (clicks, CPC, cost per lead, revenue)"}</button>
      </div>
      <div className="hidden sm:block">
        <SortTable caption="Performance by channel and campaign" rows={d.channels} columns={cols} rowKey={(r) => `${r.channel}|${r.campaign ?? ""}`}
          childRows={(r) => d.campaigns.filter((c) => c.channel === r.channel)} initialSort={{ key: "gp", dir: "desc" }} stickyFirst minWidth={more ? 1240 : 900}
          footer={<tfoot><tr className="border-t-2 border-ink bg-white">{cols.map((c, i) => (
            <td key={c.key} className={cn("px-3 py-3 font-semibold first:pl-4 last:pr-4 sm:first:pl-6 sm:last:pr-6", c.numeric && "text-right tabular-nums", i === 0 && "sticky left-0 z-[1] bg-white")}>{i === 0 ? "All channels" : c.render ? c.render(totals) : null}</td>
          ))}</tr></tfoot>} />
      </div>
      <PerfCards d={d} />
    </div>
  );
}

function exportPerf(d: PerformanceReady) {
  const usd = (c: number | null | undefined) => (c == null ? "" : (c / 100).toFixed(2));
  const x2 = (v: number | null | undefined) => (v == null ? "" : v.toFixed(2));
  // The revenue view's file has no profit column (marketing-only viewers, CEO 2026-10-22).
  const revenueView = d.measure === "revenue";
  const head = ["channel", "campaign", "spend", "clicks", "cpc", "leads", "cost_per_lead", "orders", "new_clients", "revenue", ...(revenueView ? ["roas"] : ["gross_profit", "orders_without_costs", "poas"]), "cost_to_win_a_client"];
  const rows = d.campaigns.map((r) => [r.channelLabel, r.campaign ?? "", usd(r.spendCents), r.clicks, usd(r.cpcCents), r.leads, usd(r.costPerLeadCents), r.orders, r.newCustomers, usd(r.revenueCents), ...(revenueView ? [x2(r.roas)] : [usd(r.gpCents), r.gpPendingOrders ?? 0, x2(r.poas)]), usd(r.cacCents)]);
  downloadText(`channels-campaigns-${d.period.from}-to-${d.period.to}-${d.model}-ad-clicked.csv`, toCsv(head, rows));
}

type ChannelsMode = "channels" | "website";
export function ChannelsTab() {
  const [sp, setSp] = useSearchParams();
  const view: ChannelsMode = sp.get("view") === "website" ? "website" : "channels";
  const setView = (v: ChannelsMode) => { const next = new URLSearchParams(sp); if (v === "website") next.set("view", "website"); else next.delete("view"); setSp(next, { replace: true }); };
  const [period, setPeriod] = usePeriodParam();
  const switcher = <Segmented label="View" value={view} onChange={setView} options={[{ value: "channels", label: "Channels & campaigns" }, { value: "website", label: "Website: ad → visit → order" }]} />;
  // "Go to Connections" (Website view, GA4 not connected): back to the channel view, scrolled to the strip.
  const goConnections = () => { setView("channels"); setTimeout(() => document.getElementById("connections")?.scrollIntoView({ behavior: "smooth", block: "start" }), 250); };
  if (view === "website") return <div className="space-y-6">{switcher}<WebsiteFunnel period={period} setPeriod={setPeriod} onGoConnections={goConnections} /></div>;
  return <ChannelsView period={period} setPeriod={setPeriod} switcher={switcher} />;
}

function ChannelsView({ period, setPeriod, switcher }: { period: ReturnType<typeof usePeriodParam>[0]; setPeriod: ReturnType<typeof usePeriodParam>[1]; switcher: React.ReactNode }) {
  const [model, setModel] = useState<"last" | "first">("last");
  const [uploading, setUploading] = useState(false);
  const { user } = useAuth();
  const isAdmin = !!user?.roles?.includes("admin");
  // Finance reads; uploads are admin + marketing (CEO 2026-10-21).
  const canUpload = canEditMarketing(user?.roles);
  const perf = useApi<PerformanceResponse>(`/api/marketing/performance?${periodQuery(period)}&model=${model}`);
  const d = perf.data && perf.data.ready ? perf.data : null;
  return (
    <div className="space-y-6">
      {switcher}
      <div className="flex flex-col gap-3 2xl:flex-row 2xl:items-start 2xl:justify-between">
        <PeriodPicker value={period} onChange={setPeriod} resolved={d?.period} />
        <div className="flex flex-wrap items-center gap-2">
          <Segmented label="Credit" value={model} onChange={setModel} options={[{ value: "last", label: "Credit the last ad clicked" }, { value: "first", label: "Credit the first ad clicked" }]} />
          {d && <InfoTip label="crediting" formula={d.formulas.model ?? ""} align="right" />}
        </div>
      </div>

      <Panel title="Performance by channel and campaign" caption={`${d?.measure === "revenue" ? "Revenue and ROAS" : "Gross profit and POAS"} are credited to the ${model === "last" ? "last" : "first"} ad clicked.`}
        actions={<>
          {canUpload && <button type="button" onClick={() => setUploading(true)} className="inline-flex h-10 items-center gap-1.5 rounded-md border border-rule px-3 text-label hover:bg-paper"><Upload className="h-4 w-4" aria-hidden />Upload spend</button>}
          {d && <button type="button" onClick={() => exportPerf(d)} className="inline-flex h-10 items-center gap-1.5 rounded-md border border-rule px-3 text-label hover:bg-paper"><Download className="h-4 w-4" aria-hidden />Export CSV</button>}
        </>}>
        {perf.error && <ErrorLine onRetry={perf.reload}>{perf.error}</ErrorLine>}
        {perf.loading && !perf.data && <Skeleton rows={1} label="Loading channel performance" />}
        {perf.data && !perf.data.ready && <EmptyState title="Channel numbers aren't switched on yet">{perf.data.detail}</EmptyState>}
        {d && (
          <div className={cn("space-y-3 transition-opacity", perf.loading && "opacity-60")}>
            {d.notes.map((n) => <p key={n} className="text-label text-warn">{n}</p>)}
            <PerfTable d={d} />
            <div className="space-y-1 text-meta text-ink-muted">
              {(d.totals.gpPendingOrders ?? 0) > 0 && <p>* {d.totals.gpPendingOrders} credited order{d.totals.gpPendingOrders === 1 ? " is" : "s are"} left out of gross profit until costs are entered.</p>}
              {d.later.orders > 0 && <p>Later orders from these clients (placed more than 30 days after the visit, not credited to ads): {count(d.later.orders)} · {money(d.later.revenueCents)} revenue{d.later.gpCents != null && <> · {money(d.later.gpCents)} gross profit</>}.</p>}
              <p>Spend as of the last nightly build · <span className="whitespace-nowrap">{shortDay(d.period.from)} – {shortDay(d.period.to)}</span>.</p>
            </div>
          </div>
        )}
      </Panel>

      {uploading && canUpload && <SpendUpload onClose={() => setUploading(false)} />}

      <Panel id="connections" title="Connections" info={<InfoTip label="connections" formula="Where each platform's spend comes from and when it last arrived; Klaviyo and Google Analytics are saved every night (last import, rows saved). A platform that needs attention opens up here." />}>
        <ConnectionStrip isAdmin={isAdmin} />
      </Panel>
    </div>
  );
}
